import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { 
  Zap, 
  Sun,
  TrendingUp,
  Leaf,
  Euro,
  Calendar,
  Battery,
  ArrowRight,
  LogOut,
  Calculator
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Simulator = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    installation_power_kwc: 6,
    annual_consumption_kwh: 5000,
    electricity_price: 0.25,
    community_price: 0.15,
  });

  const handleSimulate = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/simulator/calculate`, formData);
      setResult(response.data);
      toast.success('Simulation calculée !');
    } catch (error) {
      console.error('Error calculating simulation:', error);
      toast.error('Erreur lors de la simulation');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link">Communautés</Link>
              <Link to="/simulator" className="nav-link active">Simulateur</Link>
            </div>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={handleLogout}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => navigate('/login')}
                  className="btn-primary"
                  data-testid="login-btn"
                >
                  Connexion
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
              <Calculator className="w-4 h-4" />
              Simulateur de rentabilité
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Calculez vos économies
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Estimez la production de votre installation et vos économies potentielles
              en rejoignant une communauté d'autoconsommation.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <Card className="form-card" data-testid="simulator-form">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sun className="w-5 h-5 text-yellow-500" />
                  Paramètres de simulation
                </CardTitle>
                <CardDescription>
                  Ajustez les valeurs selon votre situation
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-8">
                <div className="space-y-4">
                  <Label className="flex items-center justify-between">
                    <span>Puissance de l'installation</span>
                    <span className="font-bold text-primary">{formData.installation_power_kwc} kWc</span>
                  </Label>
                  <Slider
                    value={[formData.installation_power_kwc]}
                    onValueChange={(value) => setFormData({ ...formData, installation_power_kwc: value[0] })}
                    min={1}
                    max={20}
                    step={0.5}
                    className="py-4"
                    data-testid="power-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1 kWc</span>
                    <span>20 kWc</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Label className="flex items-center justify-between">
                    <span>Consommation annuelle</span>
                    <span className="font-bold text-primary">{formData.annual_consumption_kwh.toLocaleString()} kWh</span>
                  </Label>
                  <Slider
                    value={[formData.annual_consumption_kwh]}
                    onValueChange={(value) => setFormData({ ...formData, annual_consumption_kwh: value[0] })}
                    min={1000}
                    max={20000}
                    step={100}
                    className="py-4"
                    data-testid="consumption-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1 000 kWh</span>
                    <span>20 000 kWh</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Label className="flex items-center justify-between">
                    <span>Prix du réseau actuel</span>
                    <span className="font-bold text-foreground">{formData.electricity_price.toFixed(2)} €/kWh</span>
                  </Label>
                  <Slider
                    value={[formData.electricity_price]}
                    onValueChange={(value) => setFormData({ ...formData, electricity_price: value[0] })}
                    min={0.15}
                    max={0.40}
                    step={0.01}
                    className="py-4"
                    data-testid="grid-price-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0.15 €</span>
                    <span>0.40 €</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Label className="flex items-center justify-between">
                    <span>Prix communauté</span>
                    <span className="font-bold text-primary">{formData.community_price.toFixed(2)} €/kWh</span>
                  </Label>
                  <Slider
                    value={[formData.community_price]}
                    onValueChange={(value) => setFormData({ ...formData, community_price: value[0] })}
                    min={0.05}
                    max={0.25}
                    step={0.01}
                    className="py-4"
                    data-testid="community-price-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0.05 €</span>
                    <span>0.25 €</span>
                  </div>
                </div>
                
                <Button 
                  onClick={handleSimulate}
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="simulate-btn"
                >
                  {loading ? (
                    <div className="loader w-5 h-5 border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      Calculer mes économies
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-6">
              {result ? (
                <>
                  <Card className="stats-card bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20" data-testid="result-savings">
                    <CardContent className="p-8 text-center">
                      <TrendingUp className="w-12 h-12 text-primary mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground mb-2">Économies annuelles estimées</p>
                      <p className="text-5xl font-bold text-primary mb-2">
                        {result.annual_savings_euros.toFixed(0)} €
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Retour sur investissement en {result.payback_years.toFixed(1)} ans
                      </p>
                    </CardContent>
                  </Card>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="stats-card" data-testid="result-production">
                      <CardContent className="p-6 text-center">
                        <Sun className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-foreground">
                          {result.estimated_annual_production_kwh.toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground">kWh/an produits</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="stats-card" data-testid="result-selfconsumption">
                      <CardContent className="p-6 text-center">
                        <Battery className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-foreground">
                          {result.self_consumption_rate.toFixed(0)}%
                        </p>
                        <p className="text-sm text-muted-foreground">Autoconsommation</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="stats-card" data-testid="result-co2">
                      <CardContent className="p-6 text-center">
                        <Leaf className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-foreground">
                          {result.co2_saved_kg.toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground">kg CO₂ évités/an</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="stats-card" data-testid="result-payback">
                      <CardContent className="p-6 text-center">
                        <Calendar className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-foreground">
                          {result.payback_years.toFixed(1)}
                        </p>
                        <p className="text-sm text-muted-foreground">ans de retour</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <Card className="stats-card" data-testid="result-cta">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-foreground mb-2">
                        Prêt à passer à l'action ?
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Rejoignez une communauté d'autoconsommation et commencez à économiser.
                      </p>
                      <Button 
                        onClick={() => navigate('/communities')}
                        className="btn-primary w-full"
                        data-testid="join-community-cta"
                      >
                        Trouver une communauté
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="stats-card h-full flex items-center justify-center" data-testid="result-placeholder">
                  <CardContent className="p-12 text-center">
                    <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                      <Calculator className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Lancez une simulation
                    </h3>
                    <p className="text-muted-foreground">
                      Ajustez les paramètres à gauche et cliquez sur "Calculer" pour voir vos économies potentielles.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-12 p-6 bg-muted/50 rounded-2xl">
            <h3 className="font-semibold text-foreground mb-4">À propos de cette simulation</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Production estimée basée sur 1100 kWh/kWc/an (moyenne France)</li>
              <li>• Taux d'autoconsommation calculé selon votre profil de consommation</li>
              <li>• Coût d'installation estimé à 2000 €/kWc (installation standard)</li>
              <li>• Les résultats sont indicatifs et peuvent varier selon votre situation réelle</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Simulator;
